<template>
  <div
    class="date-select-filter"
    :class="{'date-select-filter-list': appearance === 'list', 'date-select-filter-selector': appearance !== 'list', 'date-select-filter-small': size === 'small'}"
  >
    <span
      v-if="icon"
      class="date-select-filter--icon"
    >
      <img
        :src="require('@/assets/img/icons/icon-calendar.svg')"
        alt="search"
      >
    </span>
    <multi-select-input
      :value="dateSelected"
      :options.sync="periodsFiltered"
      :placeholder="$t(`${placeholder}`)"
      emitFullValue
      @inputSelected="setDate($event)"
    ></multi-select-input>
    <div
      v-show="rangeIsSelected"
      v-on-clickaway="handleClickAway"
      class="Form-field date-select-filter--container" :class="{'px-3' : regularExport}"
    >
      <template v-if="!regularExport">
      <div class="date-select-filter--inputs">
        <input-facade
          class="date-select-filter--input after"
          type="text"
          v-model="inputAfter"
          :mask="mask"
          :formatter="formatDate"
          @blur="setDateInput('date[after]', inputAfter)"
        />
        <span>{{$t('date_select_personalized_range_from_to')}}</span>
        <input-facade
          class="date-select-filter--input before"
          type="text"
          v-model="inputBefore"
          :mask="mask"
          :formatter="formatDate"
          @blur="setDateInput('date[before]', inputBefore)"
        />
      </div>
      <date-picker-input
        v-if="!resetDatePicker"
        class="date-select-filter--date-picker"
        :value="selectedRange"
        :placeholder="'date_picker_input_transaction_date_range_selector'"
        onlyPicker
        parentFormField
        @input="setDateRange"
        isRange
        :maxDate="new Date()"
      >
      </date-picker-input>
      </template>
      <template v-else>
        <!-- REGULAR EXPORT ONLY -->
        <div class="d-flex flex-column">
        <div class="date-select-filter--inputs">
          <div class="d-flex flex-column ml-0">
            <label class="form--label">
              {{$t('FILTER_FROM').toUpperCase()}}
            </label>
            <date-picker-input
              :mode="'single'"
              :placeholder="'export_exportation_flow_date'"
              class="date-select-filter--input ml-0"
              :value="startDate"
              :minDate="minDateAllowed"
              :maxDate="endDate"
              @inputDate="setStartDate($event)"
             ></date-picker-input>
          </div>
          <div class="d-flex flex-column mr-2">
          <label class="form--label ml-2">
            {{$t('FILTER_TO').toUpperCase()}}
           </label>
              <date-picker-input
                :mode="'single'"
                :placeholder="'export_exportation_flow_date'"
                class="date-select-filter--input ml-2"
                :value="endDate"
                :maxDate="maxDateAllowed"
                :minDate="startDate"
                @inputDate="setEndDate($event)"
              ></date-picker-input>
          </div>
        </div>
         <span
           class="clear-dates my-2"
           @click="clearDates"
           >{{ $t('export_exportation_flow_clear_dates') }}</span>

        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { InputFacade, facade, filter } from 'vue-input-facade'
import { mixin as clickaway } from 'vue-clickaway'

import DatePickerInput from '@/components/Commons/Forms/DatePickerInput'
import MultiSelectInput from '@/components/Commons/Forms/MultiSelectInput'
import transactionPeriodsMixin from '@/modules/transactions/_mixins/_transactionPeriods'

export default {
  name: 'DateSelect',
  components: {
    DatePickerInput,
    MultiSelectInput,
    InputFacade
  },
  props: {
    placeholder: {
      type: String,
      default: 'date_picker_input_transaction_date_range_selector'
    },
    appearance: {
      type: String,
      default: ''
    },
    periodDefault: {
      type: String,
      default: undefined
    },
    hiddenPeriods: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'normal'
    },
    icon: {
      type: Boolean,
      default: true
    },
    initialize: {
      type: Boolean,
      default: true
    },
    initialDate: {
      type: Object,
      default: () => { }
    },
    regularExport: {
      type: Boolean,
      default: false
    }
  },
  mixins: [transactionPeriodsMixin, clickaway],
  directives: { facade },
  filters: { facade: filter },
  computed: {
    mask () {
      return '##/##/####'
    },
    periodsFiltered () {
      return this.periods.filter(item => this.hiddenPeriods.indexOf(item.id) === -1)
    },
    isImpersonating () {
      return this.$store.getters['LoginStore/isImpersonating']
    }
  },
  data () {
    return {
      filters: {},
      dateSelected: [],
      selectedRange: null,
      rangeIsSelected: false,
      inputAfter: null,
      inputBefore: null,
      resetDatePicker: false,
      startDate: null,
      endDate: null,
      minDate: null,
      maxDateAllowed: null,
      minDateAllowed: null
    }
  },
  methods: {
    handleClickAway () {
      this.rangeIsSelected = false
    },
    formatDate (value, event) {
      // do not format on deletion, this could leave the input in bad state
      // but allows user to delete the leading 0 if needed for some reason
      if (event.inputType !== 'deleteContentBackward') {
        const day = value.masked.split('/')[0]
        const month = value.masked.split('/')[1]
        if (day > 31) {
          return '0' + value.unmasked
        }
        if (month > 12) {
          return day + '0' + value.unmasked.replace(day, '')
        }
      }
    },
    setDate (event) {
      this.rangeIsSelected = false
      this.dateSelected = event
      if (event.id !== 'personalized_range') {
        this.filters = { ...this.filters, ...event.filter }
        this.resetDatePicker = true
        this.selectedRange = null
        this.setFilters()
      } else {
        this.resetDatePicker = false
        this.rangeIsSelected = true
        this.dateSelected = { name: this.$t('date_select_personalized_range'), id: 'personalized_range' }
        this.$forceUpdate()
      }
    },
    setDateRange (event) {
      this.filters['date[after]'] = this.$moment.utc(event.start).startOf('day').format()
      this.filters['date[before]'] = this.$moment.utc(event.end).endOf('day').format()
      this.inputAfter = this.$moment.utc(this.filters['date[after]']).format('DD-MM-YYYY')
      this.inputBefore = this.$moment.utc(this.filters['date[before]']).format('DD-MM-YYYY')
      this.rangeIsSelected = false
      this.dateSelected = {
        name: this.$t('date_picker_select_range_selector_range_selected', { startDate: this.$moment.utc(this.filters['date[after]']).format('DD-MM-YYYY'), endDate: this.$moment.utc(this.filters['date[before]']).format('DD-MM-YYYY') }),
        id: 'custom',
        default: false,
        filter: {
          'date[after]': this.$moment.utc(event.start).startOf('day').format(),
          'date[before]': this.$moment.utc(event.end).endOf('day').format()
        }
      }
      if (this.filters['date[after]'] && this.filters['date[before]']) {
        this.setFilters()
      }
    },
    setFilters () {
      this.$emit('selectDate', this.dateSelected)
    },
    setDateInput (input, value) {
      if (value && value.length === 8 && this.$moment.utc(value, 'DDMMYYYY').isValid()) {
        this.filters[input] = this.$moment.utc(value, 'DDMMYYYY').format()
        if (this.filters['date[after]'] && this.filters['date[before]'] && this.inputAfter && this.inputBefore) {
          this.setDateRange({ start: this.filters['date[after]'], end: this.filters['date[before]'] })
        }
      }
    },
    setStartDate (event) {
      if (event) {
        this.startDate = this.$moment(event).toDate()
        this.setMaxDateAllowed()
        this.filters['date[after]'] = this.startDate
        if (this.startDate && this.endDate) {
          this.dateSelected = {
            name: this.$t('date_picker_select_range_selector_range_selected', { startDate: this.$moment(this.filters['date[after]']).format('DD-MM-YYYY'), endDate: this.$moment(this.filters['date[before]']).format('DD-MM-YYYY') }),
            id: 'custom',
            default: false,
            filter: {
            'date[after]': this.$moment(this.startDate).startOf('day').format(),
            'date[before]': this.$moment(this.endDate).endOf('day').format()
          }
          }
          this.setFilters()
        }
      }
    },
    setEndDate (event) {
      if (event) {
        this.endDate = this.$moment(event).toDate()
        this.filters['date[before]'] = this.endDate
        this.setMinDateAllowed()
        if (this.startDate && this.endDate) {
          this.dateSelected = {
            name: this.$t('date_picker_select_range_selector_range_selected', { startDate: this.$moment(this.filters['date[after]']).format('DD-MM-YYYY'), endDate: this.$moment(this.filters['date[before]']).format('DD-MM-YYYY') }),
            id: 'custom',
            default: false,
            filter: {
            'date[after]': this.$moment(this.startDate).startOf('day').format(),
            'date[before]': this.$moment(this.endDate).endOf('day').format()
          }
          }
          this.setFilters()
        }
      }
    },
    setMaxDateAllowed () {
      if (!this.startDate) {
         this.maxDateAllowed = new Date()
      }
      if (!this.isImpersonating) {
        this.maxDateAllowed = this.$moment(this.startDate).add('3', 'months').toDate()
        this.minDateAllowed = this.$moment(this.startDate).subtract('3', 'months').toDate()
      }
    },
    setMinDateAllowed () {
      if (!this.endDate) {
        this.minDateAllowed = null
      }
      if (!this.isImpersonating) {
        this.minDateAllowed = this.$moment(this.endDate).subtract('3', 'months').toDate()
      }
    },
    clearDates () {
      this.startDate = null
      this.endDate = null
      this.maxDateAllowed = null
      this.minDateAllowed = null
      this.dateSelected = { name: this.$t('date_select_personalized_range'), id: 'personalized_range' }
    }

  },
  created () {
    if (this.initialize) {
      if (this.periodDefault === 'noDefault') return
      this.dateSelected = this.periodsFiltered.find(el => {
        if (this.$route.query && this.$route.query.selectedPeriod) {
          return el.id === (this.$route.query && this.$route.query.selectedPeriod)
        } else if (this.periodDefault) {
          return this.periodDefault === el.id
        } else {
          return el.default
        }
      })
      this.$emit('emitInitialValue', this.dateSelected)
    } else {
      this.dateSelected = this.initialDate
    }
  }
}
</script>

<style lang="scss">
.date-select-filter {
  position: relative;
  background-color: var(--grey-action-3-active);
  border-radius: 4px;
  .multiselect {
    color: var(--text-body);
    &__tags {
      background-color: var(--grey-action-3-active);
    }
  }
  &.date-select-filter-list {
    background-color: var(--fill-main-background);
    .date-select-filter--icon {
      display: none;
    }
    .multiselect {
      padding-left: 0;
      &__tags {
        padding-right: 16px;
        background-color: var(--white);
        overflow: initial;
      }
      &__single {
        max-width: 100%;
      }
      &__content-wrapper {
        width: auto;
      }
      .caret {
        right: -4px;
        height: 40px;
        width: auto;
      }
    }
  }
  &.date-select-filter-selector {
    min-width: 229px;
    width: 229px;
    height: 40px;
    min-height: 40px;
    margin-right: 16px;
  }
  &--icon {
    width: 16px;
    height: 16px;
    line-height: 0;
    top: 12px;
    left: 14px;
    position: absolute;
  }
  &--container {
    position: absolute;
    margin: 5px 0 0 0;
    padding: 16px;
    padding-bottom: 0;
    z-index: 1;
    background: var(--white);
    border: 1px solid #eeeff0;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    @media screen and (max-width: 576px) {
      right: 0;
    }
  }
  &--inputs {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &--date-picker {
    .vc-container {
      border: none;
      width: 100%;
      .vc-weeks {
        padding: 0;
      }
      .vc-arrows-container {
        padding: 8px 4px;
      }
    }
  }
  &--input {
    width: 100%;
    height: 40px;
    border: none;
    appearance: none;
    --webkit-appearance: none;
    background-color: var(--grey-action-3-active);
    border-radius: 4px;
    font-weight: 300;
    font-size: 15px;
    padding: 10px;
    margin: 0 15px 0 5px;
    &.before {
      margin: 0 5px 0 15px;
    }
  }
  .multiselect {
    box-sizing: border-box;
    padding-left: 40px;
    .caret {
      right: 2px;
      height: 40px;
    }
    &__content-wrapper {
      left: 0;
    }
    &__placeholder {
      color: var(--gray);
      line-height: 40px;
      height: 40px;
      padding: 0;
      margin: 0;
      padding-left: 0;
    }
    &__tags,
    &__single {
      line-height: 40px;
      height: 40px;
      min-height: 40px;
      padding-left: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.date-select-filter {
  &.date-select-filter-small {
    min-width: 200px;
    width: 200px;
    height: 32px;
    min-height: 32px;
    margin-right: 12px;
    .date-select-filter--input {
      height: 32px;
    }
    .multiselect {
      padding-left: 12px;
      height: 32px;
      min-height: 32px;
      max-height: 32px;
      .caret {
        height: 32px;
        min-height: 32px;
        max-height: 32px;
      }
      &__placeholder {
        line-height: 32px;
        height: 32px;
      }
      &__tags,
      &__single {
        line-height: 32px;
        height: 32px;
        min-height: 32px;
      }
    }
  }
}

@media screen and (max-width: 576px) {
  .date-select-filter.date-select-filter-selector {
    width: 100%;
  }
  .date-select-filter .multiselect__content-wrapper {
    right: 0px;
    left: unset;
  }
}
</style>
<style lang="scss" scoped>
.icon {
  height: 24px;
  width: 24px;
}
.Fuell-Text--date-selector {
  font-size: 0.9em;
}
.Form-field--left {
  margin: 8px 2px 8px 0;
}
.Form-field--right {
  margin: 8px 0 8px 2px;
}
.clear-dates {
  cursor: pointer;
  margin: 0;
  margin-top: -8px;
  color: var(--text-link);
  font-weight: 500;
  font-size: 13px;
  line-height: 24px;
  width:fit-content;
}

</style>
